import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fetchTimeJobChoices, type TimeEntry, type TimeJob, type TimeJobChoice, type TimeMoveAccess } from '@/data/scheduleTimeTracking';
import { safeErrorMessage } from '@/utils/appErrors';
import SearchSelect, { type SearchSelectOption } from '../form/SearchSelect';
import { useDebouncedSearch } from './useDebouncedSearch';
import { useTimeJobLists } from './useTimeJobLists';

dayjs.extend(utc);
dayjs.extend(timezone);

const jobKey = (job: Pick<TimeJob, 'kind' | 'id'>) => `${job.kind}:${job.id}`;
const CLOSED = new Set(['completed', 'closed', 'cancelled', 'lost']);

export function timeJobKindLabel(job: Pick<TimeJob, 'kind' | 'ticket_type'>): string {
  return job.kind === 'installation' ? 'Installation' : job.ticket_type === 'asset_repair' ? 'Asset service' : 'External service';
}

export function timeRangeLabel(starts: string | null | undefined, ends: string | null | undefined, timeZone: string): string {
  return [starts, ends].filter((value): value is string => Boolean(value)).map(value => dayjs(value).tz(timeZone).format('h:mm A')).join(' – ');
}

function peopleLabel(job: TimeJobChoice): string {
  const extra = job.people_count - job.people.length;
  return job.people.join(', ') + (extra > 0 ? ` +${extra}` : '');
}

/**
 * Type-to-filter job picker for moving recorded time. Jobs on the schedule around the entry's date come first
 * (the person's own that day, others' that day, that Mon–Sun week), then every other job at the location.
 * Each search runs on the server, so it finds jobs that are not loaded yet.
 */
export default function TimeJobFinder({ id, entry, access, locationId, authorizationKey, timeZone, onChoose, disabled, inputRef }: {
  id: string; entry: TimeEntry; access: TimeMoveAccess; locationId: string; authorizationKey: string; timeZone: string;
  onChoose: (job: TimeJob | TimeJobChoice) => void; disabled?: boolean; inputRef?: React.Ref<HTMLInputElement>;
}) {
  const [search, setSearch] = React.useState('');
  const term = useDebouncedSearch(search);
  const scheduled = useQuery({
    queryKey: ['schedule-time-job-choices', authorizationKey, entry.entry_id, term],
    queryFn: () => fetchTimeJobChoices(entry.entry_id, term),
    placeholderData: keepPreviousData, staleTime: 30_000,
  });
  // A worker moving their own running clock sees what clock-in offers; managers see every job, completed included.
  const others = useTimeJobLists({ source: access === 'manager' ? 'manual' : 'clock', locationId, authorizationKey, search: term, enabled: true });
  const data = scheduled.data;
  const firstName = (data?.person_name ?? entry.person_name).split(' ')[0] || entry.person_name;
  const day = data ? dayjs(data.day).format('ddd, MMM D') : '';
  const week = data ? `${dayjs(data.week_start).format('MMM D')} – ${dayjs(data.week_end).format(dayjs(data.week_start).month() === dayjs(data.week_end).month() ? 'D' : 'MMM D')}` : '';
  const groupLabel = {
    mine: access === 'self' ? `Your schedule · ${day}` : `${firstName}’s schedule · ${day}`,
    day: `Others scheduled · ${day}`,
    week: `On the schedule this week · ${week}`,
  };
  const current = jobKey({ kind: entry.job_kind, id: entry.job_id });
  const scheduledJobs = data?.jobs ?? [];
  const taken = new Set([current, ...scheduledJobs.map(jobKey)]);
  const rows: Array<{ job: TimeJob | TimeJobChoice; option: SearchSelectOption }> = [
    ...scheduledJobs.map(job => {
      const status = CLOSED.has(job.status) ? ` · ${job.status.replace(/_/g, ' ')}` : '';
      const detail = job.group === 'mine' ? [timeJobKindLabel(job), timeRangeLabel(job.starts_at, job.ends_at, timeZone)]
        : job.group === 'day' ? [timeJobKindLabel(job), peopleLabel(job), timeRangeLabel(job.starts_at, job.ends_at, timeZone)]
          : [timeJobKindLabel(job), dayjs(job.work_day).format('ddd, MMM D'), peopleLabel(job)];
      return { job, option: { id: jobKey(job), label: `${job.code} · ${job.name}`, detail: detail.filter(Boolean).join(' · ') + status, group: groupLabel[job.group] } };
    }),
    ...others.choices.filter(choice => !taken.has(jobKey(choice.job))).map(({ job, kindLabel }) => ({
      job, option: { id: jobKey(job), label: `${job.code} · ${job.name}`, detail: `${kindLabel} · ${job.status.replace(/_/g, ' ')}`, group: 'All other jobs at this location' },
    })),
  ];
  const failure = scheduled.isError ? safeErrorMessage(scheduled.error, 'Scheduled jobs could not be loaded.') : others.error;
  return <SearchSelect id={id} label="Find a job" value={null} options={rows.map(row => row.option)} search={search} onSearchChange={setSearch}
    onChange={option => { const row = rows.find(item => item.option.id === option.id); if (row) onChoose(row.job); }}
    placeholder="Type a code, client or job name" highlight inputRef={inputRef}
    loading={scheduled.isPending || scheduled.isPlaceholderData || others.loading || term !== search.trim()}
    error={failure} onRetry={() => { if (scheduled.isError) void scheduled.refetch(); others.retry(); }}
    emptyText="No jobs match. Check the code, or try the client’s name."
    hasMore={others.hasMore} loadingMore={others.loadingMore} loadMoreLabel="Load more jobs" onLoadMore={others.loadMore}
    disabled={disabled} />;
}
